import type { HoursSchedule, InventoryResponse, MenuOption, ProfileField } from "@starsach/shared-types";

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const REFERENCE_TYPES = ["queue", "flow", "prompt", "hours_of_operation", "lex_bot"] as const;

const input = "rounded border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900";

/**
 * A menu is an array of objects because the Lambda block's `JSON`
 * response validation allows one (section 10.3) - so it is edited as a
 * list, and each option's target is picked from the inventory rather than
 * typed, which is what makes a typo a publish-time error instead of a
 * silent null on a live call.
 */
export function MenuEditor({
  field,
  inventory,
  onChange,
}: {
  field: Extract<ProfileField, { type: "menu" }>;
  inventory: InventoryResponse | undefined;
  onChange: (next: MenuOption[]) => void;
}) {
  const options = field.value;
  const update = (i: number, next: MenuOption) => onChange(options.map((o, j) => (j === i ? next : o)));

  return (
    <div className="mt-1 space-y-2">
      <ul className="space-y-2">
        {options.map((o, i) => (
          <li key={i} className="flex flex-wrap items-end gap-2 rounded border border-neutral-200 p-2 dark:border-neutral-800">
            <label className="text-xs">
              Key
              <input
                className={`${input} mt-1 block w-14`}
                value={o.dtmf}
                maxLength={1}
                aria-label={`Option ${i + 1} key`}
                onChange={(e) => update(i, { ...o, dtmf: e.target.value })}
              />
            </label>
            <label className="text-xs flex-1">
              Label
              <input className={`${input} mt-1 block w-full`} value={o.label} aria-label={`Option ${i + 1} label`} onChange={(e) => update(i, { ...o, label: e.target.value })} />
            </label>
            <label className="text-xs">
              Goes to
              <select
                className={`${input} mt-1 block`}
                aria-label={`Option ${i + 1} target type`}
                value={o.target.type}
                onChange={(e) => update(i, { ...o, target: { ...o.target, type: e.target.value as MenuOption["target"]["type"] } })}
              >
                {REFERENCE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label className="text-xs flex-1">
              Named
              <select
                className={`${input} mt-1 block w-full`}
                aria-label={`Option ${i + 1} target`}
                value={o.target.name}
                onChange={(e) => update(i, { ...o, target: { ...o.target, name: e.target.value } })}
              >
                {(inventory?.items ?? []).filter((it) => it.type === o.target.type).map((it) => (
                  <option key={it.name} value={it.name}>{it.name}</option>
                ))}
                {!(inventory?.items ?? []).some((it) => it.type === o.target.type && it.name === o.target.name) && (
                  <option value={o.target.name}>{o.target.name} (not in this instance)</option>
                )}
              </select>
            </label>
            <button type="button" className="text-sm text-red-700 underline dark:text-red-400" onClick={() => onChange(options.filter((_, j) => j !== i))}>
              Remove
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="rounded border border-neutral-300 px-2 py-1 text-xs dark:border-neutral-700"
        disabled={options.length >= 12}
        onClick={() => onChange([...options, { dtmf: String(options.length + 1), label: "", target: { type: "queue", name: "" } }])}
      >
        Add an option
      </button>
    </div>
  );
}

/**
 * Hours are authored as a schedule and never returned as one: the lander
 * evaluates them in the customer's account at call time and tells the
 * flow open or closed (section 10.3). What is edited here is what it
 * evaluates.
 */
export function HoursEditor({ field, onChange }: { field: Extract<ProfileField, { type: "hours" }>; onChange: (next: HoursSchedule) => void }) {
  const v = field.value;
  const holidays = v.holidays ?? [];

  return (
    <div className="mt-1 space-y-3 rounded border border-neutral-200 p-3 dark:border-neutral-800">
      <label className="block text-xs">
        Timezone
        <input
          className={`${input} mt-1 block w-64`}
          value={v.timezone}
          aria-label="Timezone"
          onChange={(e) => onChange({ ...v, timezone: e.target.value })}
        />
        <span className="mt-1 block text-neutral-500">
          An IANA zone such as Europe/London. Evaluated in your own account at call time, so an expiry lands on schedule
          even when a sync is late.
        </span>
      </label>

      <fieldset>
        <legend className="text-xs font-medium">Open hours</legend>
        <ul className="mt-1 space-y-1">
          {v.weekly.map((w, i) => (
            <li key={i} className="flex flex-wrap items-end gap-2">
              <label className="text-xs">
                Day
                <select
                  className={`${input} mt-1 block`}
                  aria-label={`Window ${i + 1} day`}
                  value={w.day}
                  onChange={(e) => onChange({ ...v, weekly: v.weekly.map((x, j) => (j === i ? { ...x, day: Number(e.target.value) } : x)) })}
                >
                  {DAYS.map((d, n) => <option key={d} value={n}>{d}</option>)}
                </select>
              </label>
              <label className="text-xs">
                Opens
                <input type="time" className={`${input} mt-1 block`} value={w.opens} aria-label={`Window ${i + 1} opens`}
                  onChange={(e) => onChange({ ...v, weekly: v.weekly.map((x, j) => (j === i ? { ...x, opens: e.target.value } : x)) })} />
              </label>
              <label className="text-xs">
                Closes
                <input type="time" className={`${input} mt-1 block`} value={w.closes} aria-label={`Window ${i + 1} closes`}
                  onChange={(e) => onChange({ ...v, weekly: v.weekly.map((x, j) => (j === i ? { ...x, closes: e.target.value } : x)) })} />
              </label>
              <button type="button" className="text-sm text-red-700 underline dark:text-red-400" onClick={() => onChange({ ...v, weekly: v.weekly.filter((_, j) => j !== i) })}>
                Remove
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          className="mt-1 rounded border border-neutral-300 px-2 py-1 text-xs dark:border-neutral-700"
          onClick={() => onChange({ ...v, weekly: [...v.weekly, { day: 1, opens: "09:00", closes: "17:00" }] })}
        >
          Add a window
        </button>
        <p className="mt-1 text-xs text-neutral-500">A window whose close is before its open runs past midnight.</p>
      </fieldset>

      <fieldset>
        <legend className="text-xs font-medium">Holidays</legend>
        <ul className="mt-1 space-y-1">
          {holidays.map((hol, i) => (
            <li key={i} className="flex flex-wrap items-end gap-2">
              <label className="text-xs">
                Date
                <input type="date" className={`${input} mt-1 block`} value={hol.date} aria-label={`Holiday ${i + 1} date`}
                  onChange={(e) => onChange({ ...v, holidays: holidays.map((x, j) => (j === i ? { ...x, date: e.target.value } : x)) })} />
              </label>
              <label className="text-xs flex-1">
                Name
                <input className={`${input} mt-1 block w-full`} value={hol.name} aria-label={`Holiday ${i + 1} name`}
                  onChange={(e) => onChange({ ...v, holidays: holidays.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })} />
              </label>
              <label className="flex items-center gap-1 text-xs">
                <input type="checkbox" checked={hol.closed} aria-label={`Holiday ${i + 1} closed all day`}
                  onChange={(e) => onChange({ ...v, holidays: holidays.map((x, j) => (j === i ? { ...x, closed: e.target.checked } : x)) })} />
                Closed all day
              </label>
              <label className="text-xs flex-1">
                Say instead
                <input className={`${input} mt-1 block w-full`} value={hol.announcement ?? ""} aria-label={`Holiday ${i + 1} announcement`}
                  onChange={(e) => onChange({ ...v, holidays: holidays.map((x, j) => (j === i ? { ...x, announcement: e.target.value } : x)) })} />
              </label>
              <button type="button" className="text-sm text-red-700 underline dark:text-red-400" onClick={() => onChange({ ...v, holidays: holidays.filter((_, j) => j !== i) })}>
                Remove
              </button>
            </li>
          ))}
        </ul>
        <button
          type="button"
          className="mt-1 rounded border border-neutral-300 px-2 py-1 text-xs dark:border-neutral-700"
          onClick={() => onChange({ ...v, holidays: [...holidays, { date: new Date().toISOString().slice(0, 10), name: "", closed: true }] })}
        >
          Add a holiday
        </button>
      </fieldset>

      <label className="block text-xs">
        Say when closed
        <input className={`${input} mt-1 block w-full`} value={v.closed_announcement ?? ""} aria-label="Closed announcement"
          onChange={(e) => onChange({ ...v, closed_announcement: e.target.value })} />
      </label>
    </div>
  );
}
